import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { DataTable, type Column } from '@/components/data-table/data-table';
import { queryKeys } from '@/lib/query-keys';
import { formatRelative } from '@/lib/format';
import { listTickets, getTicketStats, type Ticket } from '@/features/support/api';
import {
  SUPPORT_STATUS,
  SUPPORT_PRIORITY,
  SUPPORT_CATEGORY,
  statusTone,
  priorityTone,
} from '@/features/support/constants';

const PAGE_SIZE = 25;

const withAll = (opts: { value: string; label: string }[], allLabel: string) => [
  { value: '', label: allLabel },
  ...opts,
];

interface SupportSearch {
  status?: string;
  category?: string;
  priority?: string;
}

export const Route = createFileRoute('/_authenticated/support/')({
  validateSearch: (search: Record<string, unknown>): SupportSearch => ({
    status: typeof search['status'] === 'string' ? search['status'] : undefined,
    category: typeof search['category'] === 'string' ? search['category'] : undefined,
    priority: typeof search['priority'] === 'string' ? search['priority'] : undefined,
  }),
  component: SupportPage,
});

function SupportPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/_authenticated/support/' });

  const statsQuery = useQuery({
    queryKey: queryKeys.support.stats(),
    queryFn: getTicketStats,
  });

  const filters = {
    status: search.status,
    category: search.category,
    priority: search.priority,
  };

  const query = useInfiniteQuery({
    queryKey: queryKeys.support.list(filters),
    queryFn: ({ pageParam }) =>
      listTickets({
        status: search.status || undefined,
        category: search.category || undefined,
        priority: search.priority || undefined,
        cursor: pageParam || undefined,
        limit: PAGE_SIZE,
      }),
    initialPageParam: '' as string,
    getNextPageParam: (lastPage) => lastPage.cursor ?? undefined,
  });

  function patchSearch(patch: Partial<SupportSearch>) {
    void navigate({ to: '/support', search: (prev) => ({ ...prev, ...patch }) });
  }

  const rows = query.data?.pages.flatMap((p) => p.tickets ?? []) ?? [];
  const statusCounts = statsQuery.data?.stats ?? [];

  const columns: Column<Ticket>[] = [
    {
      key: 'ticket',
      header: 'Ticket',
      render: (t) => (
        <div>
          <div style={{ fontWeight: 600 }}>#{t.ticket_number}</div>
          <div className="wb-tiny">{t.category}</div>
        </div>
      ),
    },
    {
      key: 'message',
      header: 'Message',
      render: (t) => (
        <div style={{ maxWidth: 340 }}>
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {t.message}
          </div>
          <div className="wb-tiny">{t.user_display_name}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (t) => <Badge tone={statusTone(t.status)}>{t.status}</Badge>,
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (t) => <Badge tone={priorityTone(t.priority)}>{t.priority}</Badge>,
    },
    {
      key: 'comments',
      header: 'Comments',
      align: 'right',
      render: (t) => <span className="wb-mono">{t.comment_count}</span>,
    },
    {
      key: 'created',
      header: 'Opened',
      align: 'right',
      render: (t) => <span className="wb-small">{formatRelative(t.created_at)}</span>,
    },
  ];

  return (
    <div className="mx-auto max-w-6xl p-8">
      <div className="flex flex-col gap-1.5" style={{ marginBottom: 20 }}>
        <span className="wb-eyebrow">Operations</span>
        <h1 className="wb-h1">Support</h1>
        <p className="wb-sub">Triage tickets, reply, and track resolution.</p>
      </div>

      {statusCounts.length > 0 ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          {statusCounts.map((s) => (
            <Badge key={s.status} tone={statusTone(s.status)}>
              {s.status}: <span className="wb-mono">{s.count}</span>
            </Badge>
          ))}
        </div>
      ) : null}

      <div className="wb-toolbar" style={{ marginBottom: 16 }}>
        <Select
          size="sm"
          options={withAll(SUPPORT_STATUS, 'All statuses')}
          value={search.status ?? ''}
          onChange={(e) => patchSearch({ status: e.target.value || undefined })}
          style={{ width: 150 }}
        />
        <Select
          size="sm"
          options={withAll(SUPPORT_PRIORITY, 'All priorities')}
          value={search.priority ?? ''}
          onChange={(e) => patchSearch({ priority: e.target.value || undefined })}
          style={{ width: 150 }}
        />
        <Select
          size="sm"
          options={withAll(SUPPORT_CATEGORY, 'All categories')}
          value={search.category ?? ''}
          onChange={(e) => patchSearch({ category: e.target.value || undefined })}
          style={{ width: 160 }}
        />
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(t) => t.id}
        isLoading={query.isLoading}
        error={query.error}
        onRetry={() => query.refetch()}
        onRowClick={(t) => navigate({ to: '/support/$id', params: { id: t.id } })}
        empty={{ title: 'No tickets found', hint: 'Try a different filter.' }}
      />

      {query.hasNextPage ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
          >
            {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
